import React, { useCallback, useState } from 'react';
import { Lightbulb, Save, RefreshCw, Download, Calculator, Building, FloorPlan } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import RoomParametersSection from './RoomParametersSection';
import LampParametersSection from './LampParametersSection';
import ReflectionCoefficientsSection from './ReflectionCoefficientsSection';
import CalculationMethodSection from './CalculationMethodSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета освещённости
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  roomTypes,
  lampTypes,
  activityTypes,
  saveLighting,
  russianLightingStandards,
  customIlluminance,
  setCustomIlluminance
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveLightingName, setSaveLightingName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем значения при изменении типа помещения
      if (name === 'roomType') {
        const selectedRoomType = roomTypes.find(type => type.id === fieldValue);
        if (selectedRoomType && selectedRoomType.id !== 'custom') {
          newState.targetIlluminance = selectedRoomType.defaultIlluminance.toString();
        } else if (selectedRoomType && selectedRoomType.id === 'custom') {
          newState.targetIlluminance = customIlluminance.toString();
        }
      }

      // Обновляем значения при изменении типа лампы
      if (name === 'lampType') {
        const selectedLampType = lampTypes.find(type => type.id === fieldValue);
        if (selectedLampType) {
          newState.lampEfficiency = selectedLampType.defaultEfficiency.toString();
          newState.lampPower = selectedLampType.defaultLampPower.toString();
          newState.lampFlux = selectedLampType.defaultFlux.toString();
          // Дополнительные настройки для пользовательского типа лампы
          newState.customLampEnabled = fieldValue === 'custom';
        }
      }

      // Обновляем светой поток при изменении мощности или эффективности
      if (name === 'lampPower' || name === 'lampEfficiency') {
        const power = parseFloatOrDefault(name === 'lampPower' ? value : prev.lampPower, 0);
        const efficiency = parseFloatOrDefault(name === 'lampEfficiency' ? value : prev.lampEfficiency, 0);
        
        // Если оба значения корректны, рассчитываем поток
        if (power > 0 && efficiency > 0) {
          newState.lampFlux = Math.round(power * efficiency).toString();
        }
      }

      // Обновляем мощность при изменении потока и эффективности
      if (name === 'lampFlux' && prev.lampType !== 'custom') {
        const flux = parseFloatOrDefault(value, 0);
        const efficiency = parseFloatOrDefault(prev.lampEfficiency, 0);
        
        // Если оба значения корректны, рассчитываем мощность
        if (flux > 0 && efficiency > 0) {
          newState.lampPower = (flux / efficiency).toFixed(1);
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [roomTypes, lampTypes, errors, setErrors, setFormData, customIlluminance]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      roomLength, roomWidth, roomHeight, targetIlluminance,
      reflectionCeiling, reflectionWalls, reflectionFloor,
      lampType, lampPower, lampFlux, lampEfficiency, lampCount,
      maintenanceFactor, utilizationFactor
    } = formData;

    // Проверка размеров помещения
    if (!isPositiveNumber(roomLength)) newErrors.roomLength = 'Длина помещения должна быть положительным числом.';
    if (!isPositiveNumber(roomWidth)) newErrors.roomWidth = 'Ширина помещения должна быть положительным числом.';
    if (!isPositiveNumber(roomHeight)) newErrors.roomHeight = 'Высота помещения должна быть положительным числом.';
    
    // Проверка целевой освещенности
    if (!isPositiveNumber(targetIlluminance)) newErrors.targetIlluminance = 'Освещенность должна быть положительным числом.';
    
    // Проверка коэффициентов отражения (от 0 до 1)
    const ceilingReflection = parseFloatOrDefault(reflectionCeiling, -1);
    const wallsReflection = parseFloatOrDefault(reflectionWalls, -1);
    const floorReflection = parseFloatOrDefault(reflectionFloor, -1);
    
    if (ceilingReflection < 0 || ceilingReflection > 1) newErrors.reflectionCeiling = 'Коэффициент должен быть от 0 до 1.';
    if (wallsReflection < 0 || wallsReflection > 1) newErrors.reflectionWalls = 'Коэффициент должен быть от 0 до 1.';
    if (floorReflection < 0 || floorReflection > 1) newErrors.reflectionFloor = 'Коэффициент должен быть от 0 до 1.';
    
    // Проверка параметров лампы
    if (!lampType) newErrors.lampType = 'Выберите тип лампы.';
    if (!isPositiveNumber(lampPower)) newErrors.lampPower = 'Мощность лампы должна быть положительным числом.';
    if (!isPositiveNumber(lampFlux)) newErrors.lampFlux = 'Световой поток должен быть положительным числом.';
    if (!isPositiveNumber(lampEfficiency)) newErrors.lampEfficiency = 'Световая отдача должна быть положительным числом.';
    if (!isPositiveNumber(lampCount)) newErrors.lampCount = 'Количество ламп должно быть положительным числом.';
    
    // Проверка коэффициентов
    const maintenance = parseFloatOrDefault(maintenanceFactor, -1);
    const utilization = parseFloatOrDefault(utilizationFactor, -1);
    
    if (maintenance <= 0 || maintenance > 1) newErrors.maintenanceFactor = 'Коэффициент должен быть больше 0 и не более 1.';
    if (utilization <= 0 || utilization > 1) newErrors.utilizationFactor = 'Коэффициент должен быть больше 0 и не более 1.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета освещенности методом коэффициента использования
  const calculateLumenMethod = (roomArea, targetIlluminance, lampFlux, maintenanceFactor, utilizationFactor) => {
    // Формула: N = (E * S) / (Ф * η * Kз)
    // где N - количество светильников
    // E - требуемая освещенность (lx)
    // S - площадь помещения (м²)
    // Ф - световой поток одной лампы (lm)
    // η - коэффициент использования
    // Kз - коэффициент запаса (maintenance factor)
    
    return (targetIlluminance * roomArea) / (lampFlux * utilizationFactor * maintenanceFactor);
  };

  // Расчет индекса помещения
  const calculateRoomIndex = (roomLength, roomWidth, roomHeight, workingPlaneHeight = 0.8) => {
    const roomArea = roomLength * roomWidth;
    const calculationHeight = roomHeight - workingPlaneHeight;
    const perimeter = 2 * (roomLength + roomWidth);
    
    return roomArea / (calculationHeight * perimeter);
  };

  // Расчет коэффициента использования на основе индекса помещения и коэффициентов отражения
  const estimateUtilizationFactor = (roomIndex, reflectionCeiling, reflectionWalls) => {
    // Упрощенная таблица коэффициентов использования
    // Реальные значения зависят от типа светильника и других факторов
    let baseCoefficient;
    
    if (roomIndex < 0.6) baseCoefficient = 0.35;
    else if (roomIndex < 0.8) baseCoefficient = 0.40;
    else if (roomIndex < 1.0) baseCoefficient = 0.45;
    else if (roomIndex < 1.25) baseCoefficient = 0.50;
    else if (roomIndex < 1.5) baseCoefficient = 0.55;
    else if (roomIndex < 2.0) baseCoefficient = 0.60;
    else if (roomIndex < 2.5) baseCoefficient = 0.65;
    else if (roomIndex < 3.0) baseCoefficient = 0.67;
    else if (roomIndex < 4.0) baseCoefficient = 0.70;
    else baseCoefficient = 0.72;
    
    // Корректировка с учетом коэффициентов отражения
    // Базовые коэффициенты обычно даются для потолка 0.7, стен 0.5
    const ceilingFactor = reflectionCeiling / 0.7;
    const wallsFactor = reflectionWalls / 0.5;
    
    return baseCoefficient * Math.sqrt(ceilingFactor * wallsFactor);
  };

  // Функция расчета
  const calculateLighting = () => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const roomLength = parseFloatOrDefault(formData.roomLength);
        const roomWidth = parseFloatOrDefault(formData.roomWidth);
        const roomHeight = parseFloatOrDefault(formData.roomHeight);
        const targetIlluminance = parseFloatOrDefault(formData.targetIlluminance);
        const roomArea = roomLength * roomWidth;
        const roomPerimeter = 2 * (roomLength + roomWidth);
        const roomVolume = roomArea * roomHeight;
        
        const reflectionCeiling = parseFloatOrDefault(formData.reflectionCeiling);
        const reflectionWalls = parseFloatOrDefault(formData.reflectionWalls);
        const reflectionFloor = parseFloatOrDefault(formData.reflectionFloor);
        
        const lampFlux = parseFloatOrDefault(formData.lampFlux);
        const lampPower = parseFloatOrDefault(formData.lampPower);
        const lampEfficiency = parseFloatOrDefault(formData.lampEfficiency);
        const maintenanceFactor = parseFloatOrDefault(formData.maintenanceFactor);
        const utilizationFactor = parseFloatOrDefault(formData.utilizationFactor);
        
        // Расчет индекса помещения
        const roomIndex = calculateRoomIndex(roomLength, roomWidth, roomHeight);
        
        // Оценка коэффициента использования, если пользователь не задал его
        const estimatedUtilizationFactor = estimateUtilizationFactor(roomIndex, reflectionCeiling, reflectionWalls);
        const finalUtilizationFactor = formData.calculationMethod === 'automatic' ? 
          estimatedUtilizationFactor : utilizationFactor;
        
        // Расчет необходимого количества светильников
        const requiredLampCount = calculateLumenMethod(
          roomArea, 
          targetIlluminance, 
          lampFlux, 
          maintenanceFactor, 
          finalUtilizationFactor
        );
        
        // Округляем количество светильников до целого числа
        const recommendedLampCount = Math.ceil(requiredLampCount);
        
        // Расчет реальной освещенности с учетом округления
        const actualIlluminance = (recommendedLampCount * lampFlux * finalUtilizationFactor * maintenanceFactor) / roomArea;
        
        // Расчет удельной мощности освещения
        const specificPower = (recommendedLampCount * lampPower) / roomArea;
        
        // Расчет равномерного распределения светильников
        const idealLampDistanceX = Math.sqrt(roomArea / requiredLampCount) * 1.5;
        const idealLampDistanceY = Math.sqrt(roomArea / requiredLampCount) * 1.5;
        
        const lampsInRow = Math.ceil(roomLength / idealLampDistanceX);
        const lampsInColumn = Math.ceil(roomWidth / idealLampDistanceY);
        
        const actualLampCount = lampsInRow * lampsInColumn;
        const adjustedLampDistanceX = roomLength / (lampsInRow + 1);
        const adjustedLampDistanceY = roomWidth / (lampsInColumn + 1);
        
        // Расположение светильников для визуализации
        const lampPositions = [];
        for (let i = 1; i <= lampsInRow; i++) {
          for (let j = 1; j <= lampsInColumn; j++) {
            lampPositions.push({
              x: adjustedLampDistanceX * i,
              y: adjustedLampDistanceY * j
            });
          }
        }
        
        // Проверка соответствия нормам освещенности
        const roomTypeId = formData.roomType;
        const activityTypeId = formData.roomActivity;
        
        let standardCompliance = "unknown";
        let minRequired = 0;
        let recommended = 0;
        
        if (russianLightingStandards[activityTypeId] && 
            russianLightingStandards[activityTypeId][roomTypeId]) {
          minRequired = russianLightingStandards[activityTypeId][roomTypeId].min;
          recommended = russianLightingStandards[activityTypeId][roomTypeId].recommended;
          
          if (actualIlluminance < minRequired) {
            standardCompliance = "below";
          } else if (actualIlluminance < recommended) {
            standardCompliance = "minimal";
          } else if (actualIlluminance <= recommended * 1.2) {
            standardCompliance = "optimal";
          } else {
            standardCompliance = "excessive";
          }
        }
        
        // Оценка энергоэффективности
        let energyEfficiency = "unknown";
        if (specificPower < 5) {
          energyEfficiency = "excellent";
        } else if (specificPower < 8) {
          energyEfficiency = "good";
        } else if (specificPower < 12) {
          energyEfficiency = "average";
        } else if (specificPower < 16) {
          energyEfficiency = "poor";
        } else {
          energyEfficiency = "very-poor";
        }
        
        // Общая потребляемая мощность
        const totalPower = recommendedLampCount * lampPower;
        
        // Дополнительные метрики
        const illuminancePerWatt = actualIlluminance / specificPower;
        
        // Составляем результаты расчета
        const calculationResults = {
          roomIndex,
          roomArea,
          roomPerimeter,
          roomVolume,
          requiredLampCount,
          recommendedLampCount,
          actualLampCount,
          actualIlluminance,
          specificPower,
          totalPower,
          lampPositions,
          lampsInRow,
          lampsInColumn,
          adjustedLampDistanceX,
          adjustedLampDistanceY,
          standardCompliance,
          minRequired,
          recommended,
          energyEfficiency,
          illuminancePerWatt,
          utilizationFactor: finalUtilizationFactor,
          maintenanceFactor
        };
        
        setResults(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      roomType: 'livingRoom',
      roomLength: '5',
      roomWidth: '4',
      roomHeight: '2.7',
      targetIlluminance: '300',
      reflectionCeiling: '0.7',
      reflectionWalls: '0.5',
      reflectionFloor: '0.3',
      lampType: 'led',
      lampEfficiency: '100',
      lampPower: '10',
      lampFlux: '1000',
      lampCount: '1',
      maintenanceFactor: '0.8',
      utilizationFactor: '0.6',
      ceilingMountType: 'standard',
      customLampEnabled: false,
      colorTemperature: '4000',
      colorRendering: '80',
      energyEfficiencyClass: 'A',
      roomActivity: 'residential',
      calculationMethod: 'lumen'
    });
    setResults(null);
    setErrors({});
    setSaveLightingName('');
    setShowSaveDialog(false);
  };

  // Экспорт расчета в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки для CSV
    const headers = [
      'Параметр', 'Значение', 'Единица измерения'
    ];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    // Данные для CSV
    const rows = [
      ['Параметры помещения', '', ''],
      ['Тип помещения', roomTypes.find(t => t.id === formData.roomType)?.name || formData.roomType, ''],
      ['Длина', formatCsvNumber(parseFloatOrDefault(formData.roomLength)), 'м'],
      ['Ширина', formatCsvNumber(parseFloatOrDefault(formData.roomWidth)), 'м'],
      ['Высота', formatCsvNumber(parseFloatOrDefault(formData.roomHeight)), 'м'],
      ['Площадь', formatCsvNumber(results.roomArea), 'м²'],
      ['Объем', formatCsvNumber(results.roomVolume), 'м³'],
      ['Индекс помещения', formatCsvNumber(results.roomIndex), ''],
      ['', '', ''],
      ['Параметры освещения', '', ''],
      ['Требуемая освещенность', formatCsvNumber(parseFloatOrDefault(formData.targetIlluminance)), 'лк'],
      ['Тип источника света', lampTypes.find(t => t.id === formData.lampType)?.name || formData.lampType, ''],
      ['Световой поток одной лампы', formatCsvNumber(parseFloatOrDefault(formData.lampFlux)), 'лм'],
      ['Мощность одной лампы', formatCsvNumber(parseFloatOrDefault(formData.lampPower)), 'Вт'],
      ['Световая отдача', formatCsvNumber(parseFloatOrDefault(formData.lampEfficiency)), 'лм/Вт'],
      ['Коэффициент использования', formatCsvNumber(results.utilizationFactor), ''],
      ['Коэффициент запаса', formatCsvNumber(results.maintenanceFactor), ''],
      ['', '', ''],
      ['Результаты расчета', '', ''],
      ['Рекомендуемое количество светильников', results.recommendedLampCount, 'шт'],
      ['Фактическое количество светильников', results.actualLampCount, 'шт'],
      ['Фактическая освещенность', formatCsvNumber(results.actualIlluminance), 'лк'],
      ['Общая мощность освещения', formatCsvNumber(results.totalPower), 'Вт'],
      ['Удельная мощность освещения', formatCsvNumber(results.specificPower), 'Вт/м²'],
      ['Светильников в ряду', results.lampsInRow, 'шт'],
      ['Рядов светильников', results.lampsInColumn, 'шт'],
      ['Расстояние между светильниками по X', formatCsvNumber(results.adjustedLampDistanceX), 'м'],
      ['Расстояние между светильниками по Y', formatCsvNumber(results.adjustedLampDistanceY), 'м']
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_освещенности.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveLighting = () => {
    if (showSaveDialog) {
      const savedResult = saveLighting(saveLightingName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveLightingName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateLighting();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Lightbulb className="w-6 h-6 text-amber-500" />
          Параметры расчета освещения
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Параметры помещения */}
        <RoomParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          roomTypes={roomTypes}
          activityTypes={activityTypes}
        />

        {/* Параметры светильников/ламп */}
        <LampParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          lampTypes={lampTypes}
        />

        {/* Коэффициенты отражения */}
        <ReflectionCoefficientsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Метод расчета */}
        <CalculationMethodSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveLightingName}
                onChange={(e) => setSaveLightingName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveLighting}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать освещение'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Экспорт расчета (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveLighting}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;